import { useState } from 'react';
import {
  Button,
  CancelIcon,
  Card,
  Chip,
  RemoveCircleOutlineIcon,
  Text,
} from '@billda/design-system';
import { format, parseISO } from 'date-fns';
import { PermissionGate } from '../../../../shared/components/gates/PermissionGate';
import { Permissions } from '@billda/permissions-ui';
import { formatCurrency } from '../../../../shared/utils/currency';
import type { Deduction } from '../../types/potDetails.types';
import { CancelDeductionDialog } from './CancelDeductionDialog';

const statusConfig: Record<
  string,
  { color: 'warning' | 'success' | 'error' | 'default'; label: string }
> = {
  Booked: { color: 'warning', label: 'Pending' },
  Approved: { color: 'success', label: 'Approved' },
  Rejected: { color: 'error', label: 'Rejected' },
  Cancelled: { color: 'default', label: 'Cancelled' },
};

interface DeductionDrawerContentProps {
  deduction: Deduction;
  onCancelled?: (message: string) => void;
}

export function DeductionDrawerContent({
  deduction,
  onCancelled,
}: DeductionDrawerContentProps) {
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const status = statusConfig[deduction.status] ?? {
    color: 'default' as const,
    label: deduction.status,
  };
  const createdDate = parseISO(deduction.createdAt);

  return (
    <div className="p-6">
      <div className="flex flex-col gap-6">
        <Card className="p-4">
          <div className="mb-4 flex items-center gap-4">
            <RemoveCircleOutlineIcon
              aria-hidden="true"
              className="size-10 text-content-error"
            />
            <div className="min-w-0 flex-1">
              <Text variant="h6">{deduction.description}</Text>
              {deduction.uniqueReference && (
                <Text variant="body2" color="secondary" className="font-mono">
                  {deduction.uniqueReference}
                </Text>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <Text variant="body2" color="secondary">
                Status
              </Text>
              <Chip
                label={status.label}
                color={status.color}
                size="sm"
                variant="outlined"
              />
            </div>
            <div className="flex justify-between">
              <Text variant="body2" color="secondary">
                Amount
              </Text>
              <Text variant="body2" className="font-medium text-content-error">
                {formatCurrency(deduction.amount)}
              </Text>
            </div>
            <div className="flex justify-between">
              <Text variant="body2" color="secondary">
                Created
              </Text>
              <Text variant="body2" className="font-medium">
                {format(createdDate, 'MMM d, yyyy')}
              </Text>
            </div>
            {deduction.siteName && (
              <div className="flex justify-between">
                <Text variant="body2" color="secondary">
                  Site
                </Text>
                <Text variant="body2" className="font-medium">
                  {deduction.siteName}
                </Text>
              </div>
            )}
            {deduction.plotReference && (
              <div className="flex justify-between">
                <Text variant="body2" color="secondary">
                  Plot
                </Text>
                <Text variant="body2" className="font-medium">
                  {deduction.plotReference}
                </Text>
              </div>
            )}
            {!deduction.siteId && !deduction.plotId && (
              <div className="flex justify-between">
                <Text variant="body2" color="secondary">
                  Scope
                </Text>
                <Text variant="body2" className="font-medium">
                  Organisation-wide
                </Text>
              </div>
            )}
          </div>
        </Card>

        {deduction.canCancel && (
          <PermissionGate permission={Permissions.Deductions.Approve}>
            <Button
              variant="outlineDanger"
              onClick={() => setCancelDialogOpen(true)}
              data-testid="cancel-deduction-button"
            >
              <CancelIcon aria-hidden="true" className="size-4" />
              Cancel Deduction
            </Button>
          </PermissionGate>
        )}
      </div>

      <CancelDeductionDialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        deduction={{
          id: deduction.id,
          uniqueReference: deduction.uniqueReference,
          description: deduction.description,
          amount: deduction.amount,
          status: deduction.status,
        }}
        onSuccess={message => {
          setCancelDialogOpen(false);
          onCancelled?.(message);
        }}
      />
    </div>
  );
}
